import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { ChevronLeft, ChevronRight, Loader2, X, Plus, Trash2 } from "lucide-react";

const ALL_CLASSES = ["PST4", "PST5", "PST6", "CMT4", "CMT5", "CMT6", "SW4", "SW5", "SW6"];
const FEE_ADMISSION_CODE = "FEE_ADMISSION";
const ACADEMIC_YEARS = ["2024/2025", "2025/2026", "2026/2027", "2027/2028"];

// Inline money input: comma-separated display, numeric state, wide enough for large figures
const MoneyInput = ({ value, onChange, placeholder = "0", className = "" }: { value: number; onChange: (n: number) => void; placeholder?: string; className?: string }) => (
  <Input
    type="text"
    inputMode="numeric"
    placeholder={placeholder}
    value={value === 0 ? "" : value.toLocaleString("en-US")}
    onChange={(e) => {
      const raw = e.target.value.replace(/[^\d]/g, "");
      onChange(raw === "" ? 0 : Number(raw));
    }}
    className={`min-w-[140px] text-right ${className}`}
  />
);

const STUDENT_TYPE_LABEL: Record<string, string> = {
  first_year: "First Year",
  continuing: "Continuing",
  custom: "Custom",
};

const computeAutoName = (studentType: string, classes: string[], academicYear: string) => {
  const stLabel = STUDENT_TYPE_LABEL[studentType] ?? "";
  let classLabel = "";
  if (classes.length === 1) classLabel = classes[0];
  else if (classes.length > 1) classLabel = Array.from(new Set(classes.map(c => c.replace(/\d+$/, "")))).join("/");
  const shortYear = academicYear.replace(/^\d{2}(\d{2})\/\d{2}(\d{2})$/, "$1/$2");
  return [stLabel, classLabel, shortYear].filter(Boolean).join(" ").trim();
};

const defaultWindowStatic = (academicYear: string, sem: "I" | "II", idx: number): { start: string; end: string } => {
  const year = parseInt(academicYear.split("/")[0]) || new Date().getFullYear();
  const ranges = sem === "I"
    ? [{ s: [year, 8, 1], e: [year, 9, 0] }, { s: [year, 10, 1], e: [year, 11, 0] }]
    : [{ s: [year + 1, 1, 1], e: [year + 1, 2, 0] }, { s: [year + 1, 3, 1], e: [year + 1, 4, 0] }];
  const r = ranges[idx] ?? ranges[ranges.length - 1];
  const fmtStart = (a: number[]) => new Date(Date.UTC(a[0], a[1] - 1, a[2])).toISOString().slice(0, 10);
  const fmtEnd = (a: number[]) => new Date(Date.UTC(a[0], a[1], 0)).toISOString().slice(0, 10);
  return { start: fmtStart(r.s), end: fmtEnd(r.e) };
};

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editingCategoryId?: string | null;
  onSaved?: () => void;
}

type StudentType = "first_year" | "continuing" | "custom";
type Component = { selected: boolean; sem1: number; sem2: number };
type FaInstallment = { installment_no: number; start_date: string; end_date: string; amount: number; label: string };
type OtherInstallment = { id: string; start_date: string; end_date: string; amount: number; label: string };

export function FeeStructureManager({ open, onOpenChange, editingCategoryId, onSaved }: Props) {
  const qc = useQueryClient();
  const isEditing = !!editingCategoryId;
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);

  const [name, setName] = useState("");
  const [studentType, setStudentType] = useState<StudentType>("first_year");
  const [classes, setClasses] = useState<string[]>([]);
  const [academicYear, setAcademicYear] = useState(new Date().getFullYear() + "/" + (new Date().getFullYear() + 1));
  const [notes, setNotes] = useState("");
  const [components, setComponents] = useState<Record<string, Component>>({});
  // Fee & Admission installments per semester (fixed: 1&2 in Sem I, 3&4 in Sem II)
  const [faInstallments, setFaInstallments] = useState<Record<"I" | "II", FaInstallment[]>>({ I: [], II: [] });
  // Other costs (free-form rows) per semester
  const [otherInstallments, setOtherInstallments] = useState<Record<"I" | "II", OtherInstallment[]>>({ I: [], II: [] });
  const [nameTouched, setNameTouched] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);

  const draftKey = `feeStructureDraft:${editingCategoryId ?? "new"}`;

  const { data: costTypes } = useQuery({
    queryKey: ["fee_cost_types"],
    queryFn: async () => {
      const { data, error } = await supabase.from("fee_cost_types").select("*").order("sort_order");
      if (error) throw error;
      return data;
    },
    enabled: open,
  });

  const feeAdmissionId = costTypes?.find(c => c.code === FEE_ADMISSION_CODE)?.id;
  // Hide first-year-only items (Uniform, Medical Equipment) when ALL selected classes are continuing (Year 5/6)
  const CONTINUING_CLASSES = ["CMT5", "CMT6", "PST5", "PST6", "SW5", "SW6"];
  const FIRST_YEAR_ONLY_CODES = ["UNIFORM", "MEDICAL_EQUIPMENT"];
  const allContinuing = classes.length > 0 && classes.every(c => CONTINUING_CLASSES.includes(c));
  const otherCostTypes = (costTypes ?? []).filter(c =>
    c.code !== FEE_ADMISSION_CODE && !(allContinuing && FIRST_YEAR_ONLY_CODES.includes(c.code))
  );

  // Reset on open (create mode) OR load existing (edit mode)
  useEffect(() => {
    if (!open) return;
    if (!editingCategoryId) {
      // Try to restore draft for new structure
      let restored = false;
      try {
        const raw = localStorage.getItem(draftKey);
        if (raw) {
          const d = JSON.parse(raw);
          setStep(d.step ?? 1);
          setName(d.name ?? "");
          setNameTouched(!!d.nameTouched);
          setStudentType(d.studentType ?? "first_year");
          setClasses(d.classes ?? []);
          setAcademicYear(d.academicYear ?? ACADEMIC_YEARS[1]);
          setNotes(d.notes ?? "");
          setComponents(d.components ?? {});
          setFaInstallments(d.faInstallments ?? { I: [], II: [] });
          setOtherInstallments(d.otherInstallments ?? { I: [], II: [] });
          setDraftRestored(true);
          restored = true;
        }
      } catch { /* ignore */ }
      if (!restored) {
        setStep(1);
        setName("");
        setNameTouched(false);
        setStudentType("first_year");
        setClasses([]);
        setAcademicYear(ACADEMIC_YEARS[1]);
        setNotes("");
        setComponents({});
        setFaInstallments({ I: [], II: [] });
        setOtherInstallments({ I: [], II: [] });
        setDraftRestored(false);
      }
      return;
    }
    (async () => {
      setLoadingEdit(true);
      try {
        const { data: cat, error: e1 } = await supabase
          .from("fee_categories").select("*, fee_structure_items(*), fee_installments(*)")
          .eq("id", editingCategoryId).single();
        if (e1) throw e1;
        setStep(1);
        setName(cat.name);
        setStudentType(cat.student_type as StudentType);
        setClasses(cat.classes ?? []);
        setAcademicYear(cat.academic_year);
        setNotes(cat.notes ?? "");
        const compMap: Record<string, Component> = {};
        for (const it of cat.fee_structure_items as any[]) {
          const c = compMap[it.cost_type_id] ?? { selected: true, sem1: 0, sem2: 0 };
          if (it.semester === "I") c.sem1 = Number(it.amount);
          if (it.semester === "II") c.sem2 = Number(it.amount);
          c.selected = true;
          compMap[it.cost_type_id] = c;
        }
        setComponents(compMap);
        const allInsts = (cat.fee_installments as any[]) ?? [];
        const fa: Record<"I" | "II", FaInstallment[]> = { I: [], II: [] };
        const other: Record<"I" | "II", OtherInstallment[]> = { I: [], II: [] };
        for (const i of allInsts) {
          const sem = (i.semester === "II" ? "II" : "I") as "I" | "II";
          if ((i.kind ?? "fee_admission") === "fee_admission") {
            fa[sem].push({
              installment_no: i.installment_no,
              start_date: i.start_date ?? i.due_date,
              end_date: i.end_date ?? i.due_date,
              amount: Number(i.amount),
              label: i.label ?? "",
            });
          } else {
            other[sem].push({
              id: i.id ?? crypto.randomUUID(),
              start_date: i.start_date ?? i.due_date,
              end_date: i.end_date ?? i.due_date,
              amount: Number(i.amount),
              label: i.label ?? "",
            });
          }
        }
        // Sort by installment_no, then renumber to match new convention (Sem I → 1,2 / Sem II → 3,4)
        fa.I.sort((a, b) => a.installment_no - b.installment_no);
        fa.II.sort((a, b) => a.installment_no - b.installment_no);
        fa.I = fa.I.map((x, i) => ({ ...x, installment_no: 1 + i }));
        fa.II = fa.II.map((x, i) => ({ ...x, installment_no: 3 + i }));
        // Ensure at least 2 FA installments per semester so legacy edits show full blocks
        const ensureTwo = (arr: FaInstallment[], sem: "I" | "II", baseNo: number): FaInstallment[] => {
          const out = [...arr];
          while (out.length < 2) {
            const w = defaultWindowStatic(academicYear, sem, out.length);
            out.push({ installment_no: baseNo + out.length, start_date: w.start, end_date: w.end, amount: 0, label: `Fee & Admission — Installment ${baseNo + out.length}` });
          }
          return out;
        };
        fa.I = ensureTwo(fa.I, "I", 1);
        fa.II = ensureTwo(fa.II, "II", 3);
        setFaInstallments(fa);
        setOtherInstallments(other);
      } catch (e: any) {
        toast({ title: "Failed to load", description: e.message, variant: "destructive" });
        onOpenChange(false);
      } finally {
        setLoadingEdit(false);
      }
    })();
  }, [open, editingCategoryId, onOpenChange]);

  const defaultWindow = (sem: "I" | "II", idx: number): { start: string; end: string } => {
    const year = parseInt(academicYear.split("/")[0]) || new Date().getFullYear();
    const ranges = sem === "I"
      ? [{ s: [year, 8, 1], e: [year, 9, 0] }, { s: [year, 10, 1], e: [year, 11, 0] }]
      : [{ s: [year + 1, 1, 1], e: [year + 1, 2, 0] }, { s: [year + 1, 3, 1], e: [year + 1, 4, 0] }];
    const r = ranges[idx] ?? ranges[ranges.length - 1];
    const fmtStart = (a: number[]) => new Date(Date.UTC(a[0], a[1] - 1, a[2])).toISOString().slice(0, 10);
    const fmtEnd = (a: number[]) => new Date(Date.UTC(a[0], a[1], 0)).toISOString().slice(0, 10);
    return { start: fmtStart(r.s), end: fmtEnd(r.e) };
  };

  // Smart defaults entering step 2 (create only): seed all known cost types as rows
  useEffect(() => {
    if (step === 2 && costTypes && !isEditing && Object.keys(components).length === 0) {
      const next: Record<string, Component> = {};
      for (const ct of costTypes) {
        next[ct.id] = { selected: true, sem1: 0, sem2: 0 };
      }
      setComponents(next);
    }
  }, [step, costTypes, isEditing, components]);

  const annualOf = (id: string) => {
    const c = components[id];
    if (!c) return 0;
    return (Number(c.sem1) || 0) + (Number(c.sem2) || 0);
  };

  const feeAdmissionSem1 = feeAdmissionId ? Number(components[feeAdmissionId]?.sem1 ?? 0) : 0;
  const feeAdmissionSem2 = feeAdmissionId ? Number(components[feeAdmissionId]?.sem2 ?? 0) : 0;

  // Auto-build Fee & Admission installments entering step 3 (only when blocks empty)
  useEffect(() => {
    if (step !== 3) return;
    setFaInstallments(prev => {
      const next = { ...prev };
      if (next.I.length === 0) {
        const per = Math.round((feeAdmissionSem1 / 2) * 100) / 100;
        next.I = [0, 1].map(i => {
          const w = defaultWindow("I", i);
          return { installment_no: 1 + i, start_date: w.start, end_date: w.end, amount: per, label: `Fee & Admission — Installment ${1 + i}` };
        });
      }
      if (next.II.length === 0) {
        const per = Math.round((feeAdmissionSem2 / 2) * 100) / 100;
        next.II = [0, 1].map(i => {
          const w = defaultWindow("II", i);
          return { installment_no: 3 + i, start_date: w.start, end_date: w.end, amount: per, label: `Fee & Admission — Installment ${3 + i}` };
        });
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Auto-generate category name from student type + classes + year (until user edits)
  useEffect(() => {
    if (isEditing) return;
    if (nameTouched) return;
    const auto = computeAutoName(studentType, classes, academicYear);
    if (auto) setName(auto);
  }, [studentType, classes, academicYear, nameTouched, isEditing]);

  // Persist draft to localStorage (create mode only)
  useEffect(() => {
    if (!open || isEditing) return;
    try {
      localStorage.setItem(draftKey, JSON.stringify({
        step, name, nameTouched, studentType, classes, academicYear, notes,
        components, faInstallments, otherInstallments,
      }));
    } catch { /* ignore quota */ }
  }, [open, isEditing, draftKey, step, name, nameTouched, studentType, classes, academicYear, notes, components, faInstallments, otherInstallments]);

  const discardDraft = () => {
    try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
    setStep(1); setName(""); setNameTouched(false); setStudentType("first_year");
    setClasses([]); setAcademicYear(ACADEMIC_YEARS[1]); setNotes("");
    setComponents({}); setFaInstallments({ I: [], II: [] }); setOtherInstallments({ I: [], II: [] });
    setDraftRestored(false);
  };

  const toggleClass = (cls: string) => setClasses(p => p.includes(cls) ? p.filter(c => c !== cls) : [...p, cls]);

  const setComponent = (id: string, patch: Partial<Component>) =>
    setComponents(p => ({ ...p, [id]: { ...(p[id] ?? { selected: true, sem1: 0, sem2: 0 }), ...patch } }));

  const addOther = (sem: "I" | "II") => {
    const w = defaultWindow(sem, 0);
    setOtherInstallments(p => ({
      ...p,
      [sem]: [...p[sem], { id: crypto.randomUUID(), start_date: w.start, end_date: w.end, amount: 0, label: "" }],
    }));
  };
  const updateOther = (sem: "I" | "II", id: string, patch: Partial<OtherInstallment>) =>
    setOtherInstallments(p => ({ ...p, [sem]: p[sem].map(r => r.id === id ? { ...r, ...patch } : r) }));
  const removeOther = (sem: "I" | "II", id: string) =>
    setOtherInstallments(p => ({ ...p, [sem]: p[sem].filter(r => r.id !== id) }));

  const updateFa = (sem: "I" | "II", idx: number, patch: Partial<FaInstallment>) =>
    setFaInstallments(p => ({ ...p, [sem]: p[sem].map((x, i) => i === idx ? { ...x, ...patch } : x) }));

  const handleSave = async () => {
    if (!name.trim() || classes.length === 0 || !academicYear.trim()) {
      toast({ title: "Missing info", description: "Name, classes and academic year are required", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      let categoryId: string;
      if (isEditing && editingCategoryId) {
        const { error } = await supabase.from("fee_categories")
          .update({ name, student_type: studentType, classes, academic_year: academicYear, notes: notes || null })
          .eq("id", editingCategoryId);
        if (error) throw error;
        categoryId = editingCategoryId;
        await supabase.from("fee_structure_items").delete().eq("category_id", categoryId);
        await supabase.from("fee_installments").delete().eq("category_id", categoryId);
      } else {
        const { data: cat, error: catErr } = await supabase
          .from("fee_categories")
          .insert({ name, student_type: studentType, classes, academic_year: academicYear, notes: notes || null })
          .select().single();
        if (catErr) throw catErr;
        categoryId = cat.id;
      }

      // Persist fee_structure_items: one row per (cost_type, semester). Always include FA even if zeros.
      const items: any[] = [];
      const ids = new Set(Object.keys(components));
      if (feeAdmissionId) ids.add(feeAdmissionId);
      for (const id of ids) {
        const v = components[id] ?? { selected: false, sem1: 0, sem2: 0 };
        const isFA = id === feeAdmissionId;
        if (!v.selected && !isFA) continue;
        items.push({ category_id: categoryId, cost_type_id: id, semester: "I", amount: Number(v.sem1) || 0, academic_year: academicYear });
        items.push({ category_id: categoryId, cost_type_id: id, semester: "II", amount: Number(v.sem2) || 0, academic_year: academicYear });
      }
      if (items.length) {
        const { error } = await supabase.from("fee_structure_items").insert(items);
        if (error) throw error;
      }

      // Build installments — Fee & Admission (kind=fee_admission) + Other (kind=other)
      const allInsts: any[] = [];
      (["I", "II"] as const).forEach(sem => {
        faInstallments[sem].forEach(i => {
          allInsts.push({
            category_id: categoryId, semester: sem, installment_no: i.installment_no,
            start_date: i.start_date, end_date: i.end_date, due_date: i.end_date,
            amount: i.amount, label: i.label, kind: "fee_admission",
          });
        });
        // Other rows: number them after FA installments to satisfy NOT NULL on installment_no
        otherInstallments[sem].forEach((i, idx) => {
          allInsts.push({
            category_id: categoryId, semester: sem, installment_no: 100 + idx,
            start_date: i.start_date, end_date: i.end_date, due_date: i.end_date,
            amount: i.amount, label: i.label, kind: "other",
          });
        });
      });
      if (allInsts.length) {
        const { error } = await supabase.from("fee_installments").insert(allInsts);
        if (error) throw error;
      }

      toast({ title: isEditing ? "Fee structure updated" : "Fee structure saved", description: name });
      qc.invalidateQueries({ queryKey: ["fee_categories"] });
      try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
      setDraftRestored(false);
      onSaved?.();
      onOpenChange(false);
    } catch (e: any) {
      toast({ title: "Save failed", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const stepLabels = ["Category", "Components", "Installments", "Review"];

  if (!open) return null;

  // Helper component for an installment block
  const FaBlock = ({ sem, target }: { sem: "I" | "II"; target: number }) => {
    const installmentTotal = faInstallments[sem].reduce((s, i) => s + (Number(i.amount) || 0), 0);
    const mismatch = Math.abs(target - installmentTotal) > 0.01;
    return (
      <div className="border rounded-md p-3">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-sm">Semester {sem} — Fee & Admission Cost (Installments {sem === "I" ? "1 & 2" : "3 & 4"})</h4>
          <span className="text-xs text-muted-foreground">Target: TZS {target.toLocaleString()}</span>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">#</TableHead>
              <TableHead>Label</TableHead>
              <TableHead className="w-36">Start Date</TableHead>
              <TableHead className="w-36">End Date</TableHead>
              <TableHead className="w-40">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {faInstallments[sem].map((inst, idx) => (
              <TableRow key={idx}>
                <TableCell>{inst.installment_no}</TableCell>
                <TableCell><Input value={inst.label} onChange={e => updateFa(sem, idx, { label: e.target.value })} /></TableCell>
                <TableCell><Input type="date" value={inst.start_date} onChange={e => updateFa(sem, idx, { start_date: e.target.value })} /></TableCell>
                <TableCell><Input type="date" value={inst.end_date} onChange={e => updateFa(sem, idx, { end_date: e.target.value })} /></TableCell>
                <TableCell><MoneyInput value={inst.amount} onChange={(n) => updateFa(sem, idx, { amount: n })} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="flex items-center justify-end gap-2 mt-1 text-sm">
          <span>Total: <strong>TZS {installmentTotal.toLocaleString()}</strong></span>
          {mismatch && <Badge variant="destructive" className="text-[10px]">≠ Sem {sem} F&A TZS {target.toLocaleString()}</Badge>}
        </div>
      </div>
    );
  };

  const OtherBlock = ({ sem }: { sem: "I" | "II" }) => {
    const total = otherInstallments[sem].reduce((s, i) => s + (Number(i.amount) || 0), 0);
    // Build list of components defined in Step 2 for this semester (amount > 0), excluding Fee & Admission core
    const semKey = sem === "I" ? "sem1" : "sem2";
    const linkable = (costTypes ?? [])
      .filter(ct => ct.code !== FEE_ADMISSION_CODE)
      .map(ct => ({ id: ct.id, name: ct.name, amount: Number(components[ct.id]?.[semKey] ?? 0) }))
      .filter(o => o.amount > 0);
    return (
      <div className="border rounded-md p-3 bg-muted/20">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-sm">Semester {sem} — Other Costs</h4>
          <Button size="sm" variant="outline" onClick={() => addOther(sem)}>
            <Plus className="h-3 w-3 mr-1" />Add row
          </Button>
        </div>
        {linkable.length === 0 && (
          <p className="text-[11px] text-muted-foreground italic mb-2">
            Tip: enter Sem {sem} amounts on Step 2 components first — they'll appear in the dropdown below.
          </p>
        )}
        {otherInstallments[sem].length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No other costs added for Semester {sem}.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[200px]">Cost / Label</TableHead>
                <TableHead className="w-36">Start Date</TableHead>
                <TableHead className="w-36">End Date</TableHead>
                <TableHead className="w-40">Amount</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {otherInstallments[sem].map(row => {
                const usedNames = new Set(otherInstallments[sem].filter(r => r.id !== row.id).map(r => r.label));
                const options = linkable.filter(o => !usedNames.has(o.name) || o.name === row.label);
                const matched = linkable.find(o => o.name === row.label);
                return (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Select
                        value={matched ? row.label : ""}
                        onValueChange={(name) => {
                          const opt = linkable.find(o => o.name === name);
                          if (opt) updateOther(sem, row.id, { label: opt.name, amount: opt.amount });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={linkable.length === 0 ? "No Step 2 components yet" : "Select component…"} />
                        </SelectTrigger>
                        <SelectContent position="popper">
                          {options.map(o => (
                            <SelectItem key={o.id} value={o.name}>
                              {o.name} — TZS {o.amount.toLocaleString()}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell><Input type="date" value={row.start_date} onChange={e => updateOther(sem, row.id, { start_date: e.target.value })} /></TableCell>
                    <TableCell><Input type="date" value={row.end_date} onChange={e => updateOther(sem, row.id, { end_date: e.target.value })} /></TableCell>
                    <TableCell>
                      <MoneyInput value={row.amount} onChange={(n) => updateOther(sem, row.id, { amount: n })} />
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={() => removeOther(sem, row.id)}>
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        {otherInstallments[sem].length > 0 && (
          <div className="flex justify-end text-sm font-medium mt-1">
            Sem {sem} other costs total: TZS {total.toLocaleString()}
          </div>
        )}
      </div>
    );
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm">
      <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-lg border bg-background p-4 shadow-lg sm:p-6">
        <button type="button" aria-label="Close" className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100" onClick={() => onOpenChange(false)}>
          <X className="h-4 w-4" />
        </button>

        <div className="mb-4">
          <h2 className="text-lg font-semibold">{isEditing ? "Edit Fee Structure" : "Manage Fee Structure"}</h2>
          <p className="text-sm text-muted-foreground">Step {step} of 4 — {stepLabels[step - 1]}</p>
        </div>

        <div className="flex gap-2 mb-3">
          {stepLabels.map((l, i) => (
            <div key={l} className={`flex-1 text-xs text-center py-1.5 rounded ${step === i + 1 ? "bg-primary text-primary-foreground" : step > i + 1 ? "bg-muted" : "bg-muted/40 text-muted-foreground"}`}>
              {i + 1}. {l}
            </div>
          ))}
        </div>

        {draftRestored && !isEditing && (
          <div className="mb-3 flex items-center justify-between gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs">
            <span>Resumed your previous draft. Continue where you left off.</span>
            <Button size="sm" variant="ghost" onClick={discardDraft}>Discard & start over</Button>
          </div>
        )}

        {loadingEdit && (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        )}

        {!loadingEdit && step === 1 && (
          <div className="space-y-4">
            <div>
              <Label>Category Name *</Label>
              <Input value={name} onChange={e => { setNameTouched(true); setName(e.target.value); }} placeholder="Auto-generated from selections below" />
              {!nameTouched && (
                <p className="text-[11px] text-muted-foreground mt-1">Auto-generated. Edit to override.</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Student Type</Label>
                <Select value={studentType} onValueChange={(v: StudentType) => { setStudentType(v); if (!isEditing) setComponents({}); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent position="popper">
                    <SelectItem value="first_year">First Year</SelectItem>
                    <SelectItem value="continuing">Continuing</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Academic Year *</Label>
                <Select value={academicYear} onValueChange={setAcademicYear}>
                  <SelectTrigger><SelectValue placeholder="Select year" /></SelectTrigger>
                  <SelectContent position="popper">
                    {ACADEMIC_YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Applicable Classes *</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {ALL_CLASSES.map(c => (
                  <label key={c} className="flex items-center gap-2 border rounded px-2 py-1.5 cursor-pointer hover:bg-accent">
                    <Checkbox checked={classes.includes(c)} onCheckedChange={() => toggleClass(c)} />
                    <span className="text-sm">{c}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} />
            </div>
          </div>
        )}

        {!loadingEdit && step === 2 && costTypes && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Enter Sem I and Sem II amounts per cost type. Annual is auto-calculated. If a cost only applies to one semester, leave the other at 0.</p>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cost Type</TableHead>
                  <TableHead className="w-36">Sem I (TZS)</TableHead>
                  <TableHead className="w-36">Sem II (TZS)</TableHead>
                  <TableHead className="w-36">Annual (TZS)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Fee & Admission first */}
                {feeAdmissionId && (() => {
                  const c = components[feeAdmissionId] ?? { selected: true, sem1: 0, sem2: 0 };
                  return (
                    <TableRow key={feeAdmissionId} className="bg-primary/5">
                      <TableCell className="font-medium">Fee & Admission Cost <Badge variant="outline" className="ml-1 text-[9px]">core</Badge></TableCell>
                      <TableCell>
                        <MoneyInput value={c.sem1} onChange={(n) => setComponent(feeAdmissionId, { selected: true, sem1: n })} />
                      </TableCell>
                      <TableCell>
                        <MoneyInput value={c.sem2} onChange={(n) => setComponent(feeAdmissionId, { selected: true, sem2: n })} />
                      </TableCell>
                      <TableCell className="font-semibold">{annualOf(feeAdmissionId).toLocaleString()}</TableCell>
                    </TableRow>
                  );
                })()}
                {/* Other cost types */}
                {otherCostTypes.map(ct => {
                  const c = components[ct.id] ?? { selected: true, sem1: 0, sem2: 0 };
                  return (
                    <TableRow key={ct.id}>
                      <TableCell>{ct.name}</TableCell>
                      <TableCell>
                        <MoneyInput value={c.sem1} onChange={(n) => setComponent(ct.id, { selected: true, sem1: n })} />
                      </TableCell>
                      <TableCell>
                        <MoneyInput value={c.sem2} onChange={(n) => setComponent(ct.id, { selected: true, sem2: n })} />
                      </TableCell>
                      <TableCell className="font-semibold">{annualOf(ct.id).toLocaleString()}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {(() => {
              const semITotal = Object.values(components).reduce((s, c) => s + (Number(c?.sem1) || 0), 0);
              const semIITotal = Object.values(components).reduce((s, c) => s + (Number(c?.sem2) || 0), 0);
              const grand = semITotal + semIITotal;
              return (
                <div className="sticky bottom-0 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-muted border-t grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                  <div className="flex justify-between sm:flex-col sm:items-start">
                    <span className="text-muted-foreground text-xs">Sem I Total</span>
                    <span className="font-semibold">TZS {semITotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between sm:flex-col sm:items-start">
                    <span className="text-muted-foreground text-xs">Sem II Total</span>
                    <span className="font-semibold">TZS {semIITotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between sm:flex-col sm:items-start sm:text-right">
                    <span className="text-muted-foreground text-xs">Grand Total (Annual)</span>
                    <span className="font-bold text-primary">TZS {grand.toLocaleString()}</span>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {!loadingEdit && step === 3 && (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Define installments per semester. Fee & Admission is split into Installments 1 & 2 (Sem I) and 3 & 4 (Sem II). Other Costs are listed separately and you may add as many rows as needed with their own date ranges.
            </p>
            <FaBlock sem="I" target={feeAdmissionSem1} />
            <OtherBlock sem="I" />
            <FaBlock sem="II" target={feeAdmissionSem2} />
            <OtherBlock sem="II" />
          </div>
        )}

        {!loadingEdit && step === 4 && costTypes && (
          <div className="space-y-3 text-sm">
            <div className="border rounded p-3">
              <div><span className="text-muted-foreground">Category:</span> <strong>{name}</strong></div>
              <div><span className="text-muted-foreground">Type:</span> {studentType.replace("_", " ")}</div>
              <div><span className="text-muted-foreground">Year:</span> {academicYear}</div>
              <div className="flex flex-wrap gap-1 mt-1">{classes.map(c => <Badge key={c} variant="secondary">{c}</Badge>)}</div>
            </div>
            <div>
              <h4 className="font-semibold mb-1">Components</h4>
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Component</TableHead><TableHead>Sem I</TableHead><TableHead>Sem II</TableHead><TableHead>Annual</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(components).filter(([id, v]) => v.selected || id === feeAdmissionId).map(([id, v]) => {
                    const ct = costTypes.find(c => c.id === id);
                    return <TableRow key={id}><TableCell>{ct?.name}</TableCell><TableCell>{Number(v.sem1).toLocaleString()}</TableCell><TableCell>{Number(v.sem2).toLocaleString()}</TableCell><TableCell className="font-semibold">{(Number(v.sem1) + Number(v.sem2)).toLocaleString()}</TableCell></TableRow>;
                  })}
                </TableBody>
              </Table>
            </div>
            <div>
              <h4 className="font-semibold mb-1">Installments</h4>
              {(["I", "II"] as const).map(sem => (
                <div key={sem} className="mb-3">
                  <div className="text-xs font-semibold text-muted-foreground mb-1">Semester {sem} — Fee & Admission</div>
                  <Table>
                    <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Label</TableHead><TableHead>Date Range</TableHead><TableHead>Amount</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {faInstallments[sem].map(i => (
                        <TableRow key={i.installment_no}><TableCell>{i.installment_no}</TableCell><TableCell>{i.label}</TableCell><TableCell>{i.start_date} → {i.end_date}</TableCell><TableCell>{i.amount.toLocaleString()}</TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {otherInstallments[sem].length > 0 && (
                    <>
                      <div className="text-xs font-semibold text-muted-foreground mb-1 mt-2">Semester {sem} — Other Costs</div>
                      <Table>
                        <TableHeader><TableRow><TableHead>Label</TableHead><TableHead>Date Range</TableHead><TableHead>Amount</TableHead></TableRow></TableHeader>
                        <TableBody>
                          {otherInstallments[sem].map(i => (
                            <TableRow key={i.id}><TableCell>{i.label || "—"}</TableCell><TableCell>{i.start_date} → {i.end_date}</TableCell><TableCell>{i.amount.toLocaleString()}</TableCell></TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-between mt-4">
          <Button variant="outline" disabled={step === 1 || saving} onClick={() => setStep(s => s - 1)}>
            <ChevronLeft className="mr-1 h-4 w-4" /> Back
          </Button>
          {step < 4 ? (
            <Button onClick={() => setStep(s => s + 1)}>Next <ChevronRight className="ml-1 h-4 w-4" /></Button>
          ) : (
            <Button onClick={handleSave} disabled={saving}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{isEditing ? "Update Structure" : "Save Structure"}</Button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
